import type {
  BoosterBoosterCollectionReadModel,
  BoosterBoosterReadModel,
  PaginatedResponseModel
} from '@r/api/generated/client.schemas.ts';
import type { BoosterDraftData } from './create-booster-form-schema.ts';
import { inferGlobalCoefficientFromBoosterReferences } from './booster-reference-tiers.ts';

type BoostersCollectionResponse =
  | BoosterBoosterCollectionReadModel[]
  | {
      data?: BoosterBoosterCollectionReadModel[];
      pagination?: PaginatedResponseModel['pagination'];
    };

/** Normalise la réponse GET /boosters (tableau ou payload paginé `{ data }`). */
export function getBoostersFromResponse(response: BoostersCollectionResponse | undefined) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  return response.data ?? [];
}

/** Extrait les métadonnées de pagination d'une réponse collection boosters. */
export function getBoostersPaginationFromResponse(
  response: BoostersCollectionResponse | undefined
) {
  if (!response || Array.isArray(response)) return null;
  return response.pagination ?? null;
}

/** Indique si la date de fin du booster est strictement avant aujourd'hui. */
export function isBoosterFinished(endDate: string | Date) {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return new Date(endDate) < startOfToday;
}

/** Indique si la date de début du booster est strictement après aujourd'hui. */
export function isBoosterUpcoming(startDate: string | Date) {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return new Date(startDate) > startOfToday;
}

/** Seuls les boosters dont la date de début est future peuvent être supprimés. */
export function canDeleteBooster(booster: BoosterBoosterCollectionReadModel) {
  if (booster.status) {
    return isBoosterUpcomingStatus(booster.status);
  }

  return isBoosterUpcoming(booster.startDate);
}

/** Indique si le booster est en cours (début atteint, fin non passée). */
export function isBoosterActive(startDate: string | Date, endDate: string | Date) {
  return hasBoosterStarted(startDate) && !isBoosterFinished(endDate);
}

/** Indique si le statut API correspond à un booster terminé. */
export function isBoosterFinishedStatus(status: string | null | undefined) {
  return status?.includes('finished') ?? false;
}

/** Indique si le statut API correspond à un booster à venir. */
export function isBoosterUpcomingStatus(status: string | null | undefined) {
  return status?.includes('planned') || status?.includes('pending') || false;
}

/** Indique si le statut API correspond à un booster en cours. */
export function isBoosterActiveStatus(status: string | null | undefined) {
  return status?.includes('active') ?? false;
}

/** Classe un booster comme terminé, en privilégiant le statut API quand il existe. */
export function isBoosterFinishedByStatus(booster: BoosterBoosterCollectionReadModel) {
  if (booster.status) {
    return isBoosterFinishedStatus(booster.status);
  }

  return isBoosterFinished(booster.endDate);
}

/** Classe un booster comme à venir, en privilégiant le statut API quand il existe. */
export function isBoosterUpcomingByStatus(booster: BoosterBoosterCollectionReadModel) {
  if (booster.status) {
    return isBoosterUpcomingStatus(booster.status);
  }

  return isBoosterUpcoming(booster.startDate);
}

/** Classe un booster comme en cours, en privilégiant le statut API quand il existe. */
export function isBoosterActiveByStatus(booster: BoosterBoosterCollectionReadModel) {
  if (booster.status) {
    return isBoosterActiveStatus(booster.status);
  }

  return isBoosterActive(booster.startDate, booster.endDate);
}

/** Indique si la date de début du booster est atteinte ou dépassée (inclusif, jour courant). */
export function hasBoosterStarted(startDate: string | Date) {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return new Date(startDate) <= startOfToday;
}

/** Convertit un booster API en brouillon formulaire (dates en objets Date locaux). */
export function boosterToDraftData(booster: BoosterBoosterReadModel): BoosterDraftData {
  return {
    title: booster.title,
    description: booster.description ?? '',
    startDate: new Date(booster.startDate),
    endDate: new Date(booster.endDate),
    coefficient: inferGlobalCoefficientFromBoosterReferences(booster.boosterReferences)
  };
}
